import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import resolveImageUrl from '../utils/resolveImageUrl';
import { getCategoryName } from '../utils/adminFields';

import heroVehicle from '../assets/AppImages/car-logos/Mercedes Benz E300 2017.jpg';

import benzLogo from '../assets/AppImages/car-logos/benz.png';
import fordLogo from '../assets/AppImages/car-logos/ford.png';
import hondaLogo from '../assets/AppImages/car-logos/honda.png';
import isuzuLogo from '../assets/AppImages/car-logos/isuzu.png';
import landRoverLogo from '../assets/AppImages/car-logos/LandRover.svg.png';
import mazdaLogo from '../assets/AppImages/car-logos/mazda.png';
import nissanLogo from '../assets/AppImages/car-logos/nissan.png';
import rangeRoverLogo from '../assets/AppImages/car-logos/rangeRover.png';
import toyotaLogo from '../assets/AppImages/car-logos/toyota.png';

const fleetImage = (filename) => `/assets/fleet/${filename}`;

const suvImage = fleetImage('isuzu-mux-white.png');
const sedanImage = fleetImage('toyota-axio.png');
const twinCabImage = fleetImage('isuzu-dmax-white.png');
const carsBlack = fleetImage('honda-fit-hybrid-silver.png');
const carsExec = fleetImage('mercedes-e300-black.png');
const corporateTransportImage = fleetImage('luxury-65-seater-bus.jpg');

const categoryShowcase = [
  {
    title: 'SUVs',
    value: 'SUV',
    badge: 'Premium',
    badgeIcon: 'bi bi-crown-fill',
    image: suvImage,
    descriptor: 'Spacious • Powerful • Versatile',
  },
  {
    title: 'Sedans',
    value: 'Sedan',
    image: sedanImage,
    descriptor: 'Elegant • Comfortable • Efficient',
  },
  {
    title: 'Executive Sedans',
    value: 'Executive Sedan',
    image: carsExec,
    descriptor: 'Luxury • Refined • First-Class',
  },
  {
    title: 'Pickups',
    value: 'Twin Cab',
    image: twinCabImage,
    descriptor: 'Tough • Reliable • All-Terrain',
  },
  {
    title: 'Economy Cars',
    value: 'Hatchback',
    badge: 'Economy',
    badgeIcon: 'bi bi-patch-check-fill',
    badgeTone: 'green',
    image: carsBlack,
    descriptor: 'Affordable • Efficient • Practical',
  },
  {
    title: 'Corporate Rental',
    value: 'Corporate Rental',
    badge: 'Group Hire',
    badgeIcon: 'bi bi-people-fill',
    image: corporateTransportImage,
    descriptor: 'Coach - Shuttle - Executive groups',
    specs: ['16-65 Seats', 'Chauffeur-ready'],
  },
];

const brandLogos = [
  { src: landRoverLogo, alt: 'Land Rover' },
  { src: isuzuLogo, alt: 'Isuzu' },
  { src: hondaLogo, alt: 'Honda' },
  { src: fordLogo, alt: 'Ford' },
  { src: benzLogo, alt: 'Mercedes-Benz' },
  { src: toyotaLogo, alt: 'Toyota' },
  { src: rangeRoverLogo, alt: 'Range Rover' },
  { src: nissanLogo, alt: 'Nissan' },
  { src: mazdaLogo, alt: 'Mazda' },
];

const normalize = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
const tokenize = (value) => normalize(value).split(' ').filter(Boolean);

function deriveYear(product) {
  const source = [product?.year, product?.modelYear, product?.name, product?.description, ...(product?.features || [])]
    .filter(Boolean)
    .join(' ');
  const match = source.match(/\b(20\d{2}|19\d{2})\b/);
  return match ? match[1] : '';
}

function getVehicleImage(product, fallbackImage = carsExec) {
  const imageValue = product?.imageURL || product?.imageUrl || product?.image || product?.photo || product?.thumbnail;
  if (!imageValue) return fallbackImage;
  try {
    return resolveImageUrl(imageValue);
  } catch (error) {
    return imageValue;
  }
}

function vehiclePath(product) {
  if (!product?.id) return '/vehicles';
  return `/vehicle/show/${product.id}/${encodeURIComponent(product.name || 'vehicle')}`;
}

function formatVehiclePrice(value) {
  const price = Number(value || 0);
  return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatVehicleDailyRate(value) {
  const price = Number(value);
  if (!Number.isFinite(price) || price <= 0) return null;

  return `US$${price.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}/day`;
}

function getAvailabilityMeta(status) {
  const raw = String(status || 'Available').trim();
  const key = normalize(raw);

  if (!raw || key === 'available') {
    return { label: 'Available Now', tone: 'available' };
  }
  if (key.includes('checked out') || key.includes('in use')) {
    return { label: 'Currently In Use', tone: 'in-use' };
  }
  if (key.includes('booking confirmed') || key.includes('rented') || key.includes('reserved')) {
    return { label: 'Rented / Reserved', tone: 'reserved' };
  }
  if (key.includes('unavailable') || key.includes('maintenance')) {
    return { label: 'Unavailable', tone: 'unavailable' };
  }

  return { label: raw, tone: 'reserved' };
}

function buildVehicleSearchText(vehicle) {
  return normalize([
    vehicle.name,
    vehicle.description,
    vehicle.category,
    vehicle.year,
    vehicle.price,
    vehicle.availability,
    vehicle.transmission,
    vehicle.seats,
    ...(vehicle.features || []),
  ].filter(Boolean).join(' '));
}

function matchesSearchQuery(vehicle, queryText) {
  const tokens = tokenize(queryText);
  if (!tokens.length) return true;
  const searchable = buildVehicleSearchText(vehicle);
  return tokens.every((token) => searchable.includes(token));
}

function getVehicleFeatures(vehicle) {
  const features = Array.isArray(vehicle?.features) ? vehicle.features.filter(Boolean).map(String) : [];
  const base = [
    vehicle?.year && `${vehicle.year} model`,
    vehicle?.category,
    vehicle?.transmission,
    vehicle?.availability,
  ].filter(Boolean);
  return Array.from(new Set([...features, ...base])).slice(0, 6);
}

function normalizeVehicle(product, categoryNameById) {
  const category = product.categoryName || categoryNameById.get(String(product.categoryId)) || product.category?.categoryName || 'Premium Vehicle';
  const seats = Number(product.seats || product.capacity || product.passengerCapacity || 5);
  const transmission = product.transmission || product.gearbox || 'Automatic';
  const availability = product.bookingStatus || product.booking_status || product.availability || 'Available';

  return {
    ...product,
    id: product.id,
    name: product.name || 'Premium Vehicle',
    year: deriveYear(product),
    price: Number(product.price || 0),
    category,
    availability,
    bookingStatus: availability,
    transmission,
    seats: Number.isFinite(seats) && seats > 0 ? seats : 5,
    description: product.description || 'Premium DeRoyalty vehicle available for daily hire and executive travel.',
    features: Array.isArray(product.features) ? product.features : [],
    imageSrc: getVehicleImage(product, imageForCategory(category)),
  };
}

function compareHrefFor(ids) {
  return `/vehicles/compare?compare=${ids.join(',')}`;
}

function Vehicles({ products, categories }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterCategory, setFilterCategory] = useState(searchParams.get('category') || '');
  const [filterTransmission, setFilterTransmission] = useState('');
  const [filterSeats, setFilterSeats] = useState('');
  const [filterPrice, setFilterPrice] = useState('');
  const compareIdsFromUrl = useMemo(
    () => (searchParams.get('compare') || '').split(',').map((id) => id.trim()).filter(Boolean),
    [searchParams]
  );
  const [compareItems, setCompareItems] = useState(() => new Set(compareIdsFromUrl));
  const queryText = searchParams.get('q') || '';
  const isCompareRoute = location.pathname.includes('/compare');

  useEffect(() => {
    if (isCompareRoute) {
      setCompareItems(new Set(compareIdsFromUrl));
    }
  }, [compareIdsFromUrl, isCompareRoute]);

  const categoryNameById = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((category) => {
      map.set(String(category.id), getCategoryName(category));
    });
    return map;
  }, [categories]);

  const sourceVehicles = useMemo(() => {
    const apiVehicles = Array.isArray(products) ? products : [];
    return apiVehicles.map((product) => normalizeVehicle(product, categoryNameById));
  }, [products, categoryNameById]);

  const categoryOptions = useMemo(() => {
    const categorySet = new Set();
    (categories || []).forEach((category) => {
      const name = getCategoryName(category);
      if (name) categorySet.add(name);
    });
    sourceVehicles.forEach((vehicle) => vehicle.category && categorySet.add(vehicle.category));
    return Array.from(categorySet);
  }, [categories, sourceVehicles]);

  const seatOptions = useMemo(() => {
    const seatSet = new Set();
    sourceVehicles.forEach((vehicle) => {
      const seats = Number(vehicle.seats);
      if (Number.isFinite(seats) && seats > 0) seatSet.add(seats);
    });
    return Array.from(seatSet).sort((a, b) => a - b);
  }, [sourceVehicles]);

  const filteredVehicles = useMemo(() => {
    const maxPrice = Number(filterPrice || 0);

    return sourceVehicles.filter((vehicle) => {
      const matchesQuery = matchesSearchQuery(vehicle, queryText);
      const matchesCategory = !filterCategory || normalize(vehicle.category).includes(normalize(filterCategory)) || normalize(filterCategory).includes(normalize(vehicle.category));
      const matchesTransmission = !filterTransmission || normalize(vehicle.transmission).includes(normalize(filterTransmission));
      const matchesSeats = !filterSeats || String(vehicle.seats) === String(filterSeats);
      const matchesPrice = !maxPrice || Number(vehicle.price || 0) <= maxPrice;
      return matchesQuery && matchesCategory && matchesTransmission && matchesSeats && matchesPrice;
    });
  }, [sourceVehicles, queryText, filterCategory, filterTransmission, filterSeats, filterPrice]);

  const scrollToFleetStart = (behavior = 'smooth') => {
    const fleetSection = document.getElementById('complete-fleet');
    if (!fleetSection) return;

    const navbarHeight = document.querySelector('.site-navbar')?.getBoundingClientRect().height || 92;
    const scrollTop = fleetSection.getBoundingClientRect().top + window.scrollY - navbarHeight - 24;
    window.scrollTo({
      top: Math.max(0, scrollTop),
      behavior,
    });
  };

  useEffect(() => {
    if (location.hash === '#complete-fleet') {
      const frame = window.requestAnimationFrame(() => scrollToFleetStart('auto'));
      return () => window.cancelAnimationFrame(frame);
    }
    return undefined;
  }, [location.hash, filteredVehicles.length]);

  const clearFilters = () => {
    setFilterCategory('');
    setFilterTransmission('');
    setFilterSeats('');
    setFilterPrice('');
    const params = new URLSearchParams(searchParams);
    params.delete('q');
    params.delete('category');
    setSearchParams(params);
  };

  const applyCategory = (category) => {
    setFilterCategory(category);
    const params = new URLSearchParams(searchParams);
    params.set('category', category);
    setSearchParams(params);
    requestAnimationFrame(() => {
      scrollToFleetStart();
    });
  };

  const toggleCompare = (key) => {
    setCompareItems((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const compareIds = useMemo(() => Array.from(compareItems), [compareItems]);
  const compareVehicles = useMemo(
    () => sourceVehicles.filter((vehicle) => compareItems.has(String(vehicle.id))),
    [compareItems, sourceVehicles]
  );
  const featuredVehicle = useMemo(() => (
    sourceVehicles.find((vehicle) => normalize(vehicle.name).includes('mercedes benz e300')) ||
    sourceVehicles.find((vehicle) => normalize(vehicle.category).includes('executive')) ||
    sourceVehicles[0] ||
    null
  ), [sourceVehicles]);
  const showingCount = filteredVehicles.length;
  const openVehicleDetails = (vehicle) => navigate(vehiclePath(vehicle));

  return (
    <div className="vehicles-premium-page">
      <section className="vehicles-premium-hero">
        <figure className="vehicles-hero-car" aria-label="Premium DeRoyalty executive vehicle">
          <img src={heroVehicle} alt="Premium DeRoyalty rental vehicle" />
        </figure>
        <div className="vehicles-hero-copy">
          <p className="vehicles-kicker">Premium Fleet. Unmatched Experiences.</p>
          <h1>Our <span>Vehicles</span></h1>
          <p>
            Explore our premium fleet, meticulously maintained and ready to deliver comfort,
            performance, and style for every journey.
          </p>
          <div className="vehicles-hero-actions">
            <a
              href="#complete-fleet"
              className="vehicles-btn vehicles-btn-gold"
              onClick={(event) => {
                event.preventDefault();
                scrollToFleetStart();
              }}
            >
              View All Vehicles <i className="bi bi-arrow-right-circle" />
            </a>
            <Link to={compareIds.length ? compareHrefFor(compareIds) : '/vehicles/compare'} className="vehicles-btn vehicles-btn-outline">
              Compare Vehicles <i className="bi bi-bezier2" />
            </Link>
          </div>
        </div>
      </section>

      <section className="vehicles-filter-stage" aria-label="Vehicle filters">
        <div className="vehicles-filter-control">
          <i className="bi bi-car-front" />
          <label>
            <span>Category</span>
            <select value={filterCategory} onChange={(event) => setFilterCategory(event.target.value)}>
              <option value="">All Categories</option>
              {categoryOptions.map((category) => (
                <option value={category} key={category}>{category}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="vehicles-filter-control">
          <i className="bi bi-diagram-3" />
          <label>
            <span>Transmission</span>
            <select value={filterTransmission} onChange={(event) => setFilterTransmission(event.target.value)}>
              <option value="">All Transmission</option>
              <option value="Automatic">Automatic</option>
              <option value="Manual">Manual</option>
            </select>
          </label>
        </div>

        <div className="vehicles-filter-control">
          <i className="bi bi-person-badge" />
          <label>
            <span>Seats</span>
            <select value={filterSeats} onChange={(event) => setFilterSeats(event.target.value)}>
              <option value="">All Seats</option>
              {seatOptions.map((seats) => (
                <option key={seats} value={seats}>{seats} Seats</option>
              ))}
            </select>
          </label>
        </div>

        <div className="vehicles-filter-control">
          <i className="bi bi-tag" />
          <label>
            <span>Price Range</span>
            <select value={filterPrice} onChange={(event) => setFilterPrice(event.target.value)}>
              <option value="">Any Price Range</option>
              <option value="100">Up to US$100/day</option>
              <option value="250">Up to US$250/day</option>
              <option value="500">Up to US$500/day</option>
              <option value="800">Up to US$800/day</option>
              <option value="1200">Up to US$1,200/day</option>
            </select>
          </label>
        </div>

        <button type="button" className="vehicles-clear-filters" onClick={clearFilters}>
          <i className="bi bi-arrow-counterclockwise" /> Clear Filters
        </button>
        <a
          href="#complete-fleet"
          className="vehicles-apply-filters"
          onClick={(event) => {
            event.preventDefault();
            scrollToFleetStart();
          }}
        >
          Apply Filters <i className="bi bi-search" />
        </a>
      </section>

      {compareIds.length > 0 && !isCompareRoute && (
        <section
          className="vehicles-compare-tray"
          role="status"
          aria-live="polite"
          aria-label="Selected vehicles for comparison"
        >
          <div>
            <strong>{compareIds.length} selected</strong>
            <span>Compare price, status, category, seats, transmission, and key features.</span>
          </div>
          <Link to={compareHrefFor(compareIds)} className="vehicles-btn vehicles-btn-gold">
            Compare {compareIds.length} Vehicle{compareIds.length === 1 ? '' : 's'}
            <i className="bi bi-bezier2" />
          </Link>
        </section>
      )}

      {isCompareRoute && (
        <VehicleComparePanel vehicles={compareVehicles} allVehicles={sourceVehicles} />
      )}

      <section id="vehicle-category-showcase" className="vehicles-category-showcase">
        {categoryShowcase.map((card) => (
          <article className="vehicle-category-card" key={card.title}>
            <div className="vehicle-category-image">
              {card.badge && (
                <span className={`vehicle-category-badge ${card.badgeTone === 'green' ? 'green' : ''}`}>
                  <i className={card.badgeIcon} /> {card.badge}
                </span>
              )}
              <img src={card.image} alt={`${card.title} category`} />
            </div>
            <div className="vehicle-category-content">
              <h2>{card.title}</h2>
              <p>{card.descriptor}</p>
              <div className="vehicle-category-specs">
                {(card.specs || ['5 Seats', 'Automatic']).map((spec, index) => (
                  <span key={spec}>
                    <i className={index === 0 ? 'bi bi-person' : 'bi bi-gear'} /> {spec}
                  </span>
                ))}
              </div>
              <div className="vehicle-category-footer">
                <button type="button" onClick={() => applyCategory(card.value)}>View Range</button>
              </div>
            </div>
          </article>
        ))}
      </section>

      {featuredVehicle && (
      <section className="vehicles-featured-model">
        <div className="vehicles-featured-copy">
          <p>Featured Model</p>
          <h2>{featuredVehicle.name}</h2>
          <span>{featuredVehicle.description}</span>
          <div className="vehicles-status-row">
            <VehicleStatusBadge status={featuredVehicle.availability} />
            <small>{featuredVehicle.category}</small>
          </div>
          <ul>
            {getVehicleFeatures(featuredVehicle).map((feature) => (
              <li key={feature}><i className="bi bi-check2-circle" /> {feature}</li>
            ))}
          </ul>
        </div>
        <figure className="vehicles-featured-image">
          <img src={featuredVehicle.imageSrc} alt={`${featuredVehicle.name} featured model`} />
        </figure>
        <aside className="vehicles-featured-price">
          <span>From</span>
          <strong>{formatVehiclePrice(featuredVehicle.price)}</strong>
          <em>/ day</em>
          <Link to={vehiclePath(featuredVehicle)} className="vehicles-btn vehicles-btn-gold">View Details <i className="bi bi-arrow-right-circle" /></Link>
          <button
            type="button"
            className={`vehicles-btn vehicles-btn-outline ${compareItems.has(String(featuredVehicle.id)) ? 'active' : ''}`}
            onClick={() => toggleCompare(String(featuredVehicle.id))}
          >
            <i className="bi bi-bezier2" /> {compareItems.has(String(featuredVehicle.id)) ? 'Added to Compare' : 'Add to Compare'}
          </button>
        </aside>
      </section>
      )}

      <section className="vehicles-brand-strip" aria-label="Prestigious brands">
        <div>
          <p>Drive the world's</p>
          <strong>Most Prestigious Brands</strong>
        </div>
        <div className="vehicles-brand-viewport">
          <div className="vehicles-brand-track">
            {[...brandLogos, ...brandLogos].map((logo, index) => {
              const isDuplicate = index >= brandLogos.length;
              return (
                <figure
                  key={`${logo.alt}-${index}`}
                  className="vehicles-brand-logo"
                  aria-hidden={isDuplicate ? 'true' : undefined}
                >
                  <img src={logo.src} alt={isDuplicate ? '' : logo.alt} />
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      <section id="complete-fleet" className="vehicles-complete-fleet">
        <div className="vehicles-results-heading">
          <div>
            <p>Complete Fleet</p>
            <h2>{queryText ? `Search results for “${queryText}”` : 'Vehicles Available For Hire'}</h2>
          </div>
          <div className="vehicles-results-count">
            Showing <strong>{showingCount}</strong> vehicle(s)
          </div>
        </div>

        {filteredVehicles.length > 0 ? (
          <div className="vehicles-listing-grid">
            {filteredVehicles.map((vehicle) => (
              <VehicleListingCard
                key={vehicle.id || vehicle.name}
                vehicle={vehicle}
                isCompared={compareItems.has(String(vehicle.id))}
                onToggleCompare={toggleCompare}
                onOpenDetails={openVehicleDetails}
              />
            ))}
          </div>
        ) : (
          <div className="vehicles-empty-result">
            <i className="bi bi-car-front" />
            <h3>{sourceVehicles.length ? 'No vehicles found' : 'No vehicles available'}</h3>
            <p>{sourceVehicles.length ? 'Try widening your category, seat, transmission, or price filter.' : 'The public fleet is waiting for database vehicles from the API.'}</p>
            <button type="button" className="vehicles-btn vehicles-btn-gold" onClick={clearFilters}>Clear Filters</button>
          </div>
        )}
      </section>
    </div>
  );
}

function imageForCategory(category) {
  const normalized = normalize(category);
  if (normalized.includes('corporate') || normalized.includes('coach') || normalized.includes('bus') || normalized.includes('shuttle') || normalized.includes('quantum')) return corporateTransportImage;
  if (normalized.includes('twin') || normalized.includes('pickup') || normalized.includes('offroad')) return twinCabImage;
  if (normalized.includes('sedan')) return sedanImage;
  if (normalized.includes('hatch')) return carsBlack;
  if (normalized.includes('suv')) return suvImage;
  return carsExec;
}

const categoryDescriptors = {
  'suv':              'Spacious • Powerful • Versatile',
  'executive suv':    'Prestige • Comfort • All-Terrain',
  'sedan':            'Elegant • Comfortable • Efficient',
  'executive sedan':  'Luxury • Refined • First-Class',
  'twin cab':         'Tough • Reliable • All-Terrain',
  'hatchback':        'Affordable • Efficient • Practical',
  'offroad / 4x4':    'Rugged • Capable • Adventurous',
  'corporate rental': 'Group Hire - Shuttle - Corporate',
};

function getCategoryDescriptor(category) {
  const key = normalize(category);
  for (const [match, label] of Object.entries(categoryDescriptors)) {
    if (key.includes(match.replace(' / ', ' ')) || key.includes(match)) return label;
  }
  return 'Premium • Reliable • DeRoyalty';
}

function VehicleStatusBadge({ status }) {
  const meta = getAvailabilityMeta(status);
  return <span className={`vehicle-status-badge ${meta.tone}`}>{meta.label}</span>;
}

function VehicleComparePanel({ vehicles, allVehicles }) {
  if (!vehicles.length) {
    return (
      <section className="vehicles-compare-panel" aria-label="Vehicle comparison">
        <div className="vehicles-compare-empty">
          <i className="bi bi-bezier2" />
          <h2>Choose vehicles to compare</h2>
          <p>Select vehicles from the fleet list below to compare price, availability, category, seats, transmission, and features.</p>
          <a href="/vehicles#complete-fleet" className="vehicles-btn vehicles-btn-gold">Browse Vehicles</a>
        </div>
      </section>
    );
  }

  const rows = [
    ['Status', (vehicle) => <VehicleStatusBadge status={vehicle.availability} />],
    ['Daily Rate', (vehicle) => `${formatVehiclePrice(vehicle.price)} / day`],
    ['Category', (vehicle) => vehicle.category],
    ['Model Year', (vehicle) => vehicle.year || 'Not listed'],
    ['Seats', (vehicle) => `${vehicle.seats || 5} Seats`],
    ['Transmission', (vehicle) => vehicle.transmission || 'Automatic'],
  ];

  return (
    <section className="vehicles-compare-panel" aria-label="Vehicle comparison">
      <div className="vehicles-compare-heading">
        <div>
          <p>Vehicle Insights</p>
          <h2>Compare Selected Vehicles</h2>
        </div>
        <span>{vehicles.length} of {allVehicles.length} vehicle(s) selected</span>
      </div>

      <div className="vehicles-compare-table-wrap">
        <table className="vehicles-compare-table">
          <thead>
            <tr>
              <th>Feature</th>
              {vehicles.map((vehicle) => (
                <th key={vehicle.id}>
                  <img src={vehicle.imageSrc} alt="" />
                  <strong>{vehicle.name}</strong>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, render]) => (
              <tr key={label}>
                <th>{label}</th>
                {vehicles.map((vehicle) => (
                  <td key={`${vehicle.id}-${label}`}>{render(vehicle)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <th>Key Features</th>
              {vehicles.map((vehicle) => (
                <td key={`${vehicle.id}-features`}>
                  <ul>
                    {getVehicleFeatures(vehicle).map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
            <tr>
              <th>Next Step</th>
              {vehicles.map((vehicle) => (
                <td key={`${vehicle.id}-details`}>
                  <Link to={vehiclePath(vehicle)} className="vehicle-listing-view-btn">View Details</Link>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function VehicleListingCard({ vehicle, isCompared, onToggleCompare, onOpenDetails }) {
  const cat = normalize(vehicle.category);
  const isExecutive = cat.includes('executive');
  const isOffroad = cat.includes('offroad') || cat.includes('4x4');
  const isSuv = cat.includes('suv') && !isExecutive;
  const isCorporate = cat.includes('corporate') || cat.includes('coach') || cat.includes('bus') || cat.includes('shuttle');

  const badge = isCorporate ? { label: 'Corporate', icon: 'bi bi-people-fill', tone: '' }
    : isExecutive ? { label: 'Executive', icon: 'bi bi-crown-fill', tone: '' }
    : isOffroad ? { label: 'Offroad', icon: 'bi bi-patch-check-fill', tone: 'green' }
    : isSuv ? { label: 'Premium', icon: 'bi bi-crown-fill', tone: '' }
    : null;

  const shortDesc = vehicle.description
    ? vehicle.description.split('.')[0].trim()
    : getCategoryDescriptor(vehicle.category);
  const dailyRate = formatVehicleDailyRate(vehicle.price);
  const handleCardClick = (event) => {
    if (event.target.closest('a, button, input, label')) return;
    onOpenDetails(vehicle);
  };
  const handleCardKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenDetails(vehicle);
    }
  };

  return (
    <article
      className="vehicle-listing-card"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onDoubleClick={() => onOpenDetails(vehicle)}
      onKeyDown={handleCardKeyDown}
      aria-label={`View details for ${vehicle.name}`}
    >
      <div className="vehicle-listing-image">
        {badge && (
          <span className={`vehicle-category-badge ${badge.tone}`}>
            <i className={badge.icon} /> {badge.label}
          </span>
        )}
        <img src={vehicle.imageSrc} alt={vehicle.name} loading="lazy" />
      </div>
      <div className="vehicle-listing-body">
        <h3>{vehicle.name}</h3>
        <VehicleStatusBadge status={vehicle.availability} />
        <p className="vehicle-listing-descriptor">{shortDesc}</p>
        <div className="vehicle-listing-specs">
          <span><i className="bi bi-person" /> {vehicle.seats || 5} Seats</span>
          <span><i className="bi bi-gear" /> {vehicle.transmission || 'Automatic'}</span>
          {dailyRate && (
            <strong
              className="vehicle-listing-daily-rate"
              aria-label={`Daily lease rate for ${vehicle.name}: ${dailyRate}`}
            >
              <i className="bi bi-cash-stack" /> {dailyRate}
            </strong>
          )}
        </div>
        <div className="vehicle-listing-footer">
          <Link to={vehiclePath(vehicle)} className="vehicle-listing-view-btn">View Details</Link>
          <label className="vehicle-listing-compare-label">
            <input
              type="checkbox"
              checked={isCompared}
              onChange={() => onToggleCompare(String(vehicle.id))}
              aria-label={`Compare ${vehicle.name}`}
            />
            Compare
          </label>
        </div>
      </div>
    </article>
  );
}

export default Vehicles;
